import { faClose, faFileArrowUp } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useError } from "../../../context/useErrorContext"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"

const MAXIMUM_FILES = 20

const MAXIMUM_FILE_SIZE_BYTES = 10 * 1024 * 1024

const MAXIMUM_BATCH_SIZE_BYTES = 50 * 1024 * 1024

type SelectOption = {
  label: string
  value: string
}

interface FiscalImportPopupProps {
  initialYear: number
  initialMonth: number
  yearOptions: SelectOption[]
  monthOptions: SelectOption[]
  onClose: () => void
  onImport: (files: File[], referenceYear: number, referenceMonth: number) => Promise<boolean>
}

export const FiscalImportPopup: React.FC<FiscalImportPopupProps> = ({
  initialYear,
  initialMonth,
  yearOptions,
  monthOptions,
  onClose,
  onImport,
}) => {
  const { t } = useTranslation(["fiscalIntegration", "helpPopup"])

  const { setError } = useError()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const [referenceYear, setReferenceYear] = useState(initialYear)

  const [referenceMonth, setReferenceMonth] = useState(initialMonth)

  const [isDragging, setIsDragging] = useState(false)

  const [isImporting, setIsImporting] = useState(false)

  const selectFiles = (files: File[]) => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0)

    if (
      files.length > MAXIMUM_FILES ||
      files.some((file) => file.size <= 0 || file.size > MAXIMUM_FILE_SIZE_BYTES) ||
      totalSize > MAXIMUM_BATCH_SIZE_BYTES
    ) {
      setSelectedFiles([])

      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }

      setError(t("imports.validation.title"), t("imports.validation.size"))
      return
    }

    setSelectedFiles(files)
  }

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFiles(Array.from(event.target.files ?? []))
  }

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
    selectFiles(Array.from(event.dataTransfer.files))
  }

  const handleImport = async () => {
    if (selectedFiles.length === 0 || isImporting) {
      return
    }

    setIsImporting(true)

    try {
      const imported = await onImport(selectedFiles, referenceYear, referenceMonth)

      if (imported) {
        onClose()
      }
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("imports.upload.title")}
              icon={faFileArrowUp}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.fiscal-imports"
                    title={t("helpPopup:global.fiscalImports.title")}
                    description={t("helpPopup:global.fiscalImports.description")}
                    bullets={t("helpPopup:global.fiscalImports.bullets", { returnObjects: true }) as string[]}
                    steps={t("helpPopup:global.fiscalImports.steps", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    disabled={isImporting}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalImports.closeImportPopup"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="flex flex-col gap-4 p-3 sm:p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <GenericDropDown
                    legend={t("imports.fields.referenceYear")}
                    data={yearOptions}
                    initialState={referenceYear.toString()}
                    handlerFuncion={(event) => setReferenceYear(Number(event.target.value))}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalImports.referenceYear"
                  />

                  <GenericDropDown
                    legend={t("imports.fields.referenceMonth")}
                    data={monthOptions}
                    initialState={referenceMonth.toString()}
                    handlerFuncion={(event) => setReferenceMonth(Number(event.target.value))}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalImports.referenceMonth"
                  />
                </div>

                <label
                  className={`flex min-h-44 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-5 py-6 text-center transition-all ${
                    isDragging
                      ? "border-sky-600 bg-sky-100 shadow-inner"
                      : "border-sky-300 bg-sky-50/70 hover:border-sky-500 hover:bg-sky-50"
                  }`}
                  onDragEnter={(event) => {
                    event.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={(event) => {
                    event.preventDefault()
                    setIsDragging(false)
                  }}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.xml,application/pdf,application/xml,text/xml"
                    onChange={handleFilesSelected}
                    className="sr-only"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.files"
                  />

                  <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-700 text-xl text-white shadow-sm">
                    <FontAwesomeIcon icon={faFileArrowUp} />
                  </span>

                  <strong className="text-sm text-slate-800">
                    {selectedFiles.length > 0
                      ? t("imports.upload.selected", { count: selectedFiles.length })
                      : t("imports.upload.dropTitle")}
                  </strong>

                  <span className="mt-1 text-xs text-slate-500">{t("imports.upload.dropHint")}</span>

                  <span className="mt-3 text-[11px] font-medium text-slate-500">{t("imports.upload.limits")}</span>
                </label>

                <div className="flex justify-end border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    onClick={() => void handleImport().catch(() => undefined)}
                    disabled={selectedFiles.length === 0 || isImporting}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-sky-700 px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-sky-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.import"
                  >
                    <FontAwesomeIcon icon={faFileArrowUp} />

                    {isImporting ? t("imports.upload.importing") : t("imports.upload.import")}
                  </button>
                </div>
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
